import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';

/** SAP 중간 관리 수수료 정산 — 레거시 appr_edit_forms_pay_shop_manager */
export const SHOP_MANAGER_FEE_TYPE = 157;

export const isShopManagerFeeType = (apvType: number): boolean =>
  apvType === SHOP_MANAGER_FEE_TYPE;

/** 레거시 화면에 박힌 목록 — 라벨은 번역해 제목에도 들어감(한국어 외 언어는 영문 표기) */
export const SHOP_MANAGER_FEE_BRANDS = [
  { value: 'M', label: '엠엘비' },
  { value: 'I', label: '엠엘비 키즈' },
  { value: 'X', label: '디스커버리' },
  { value: 'A', label: 'STRETCH ANGELS' },
  { value: 'V', label: 'DUVETICA' },
  { value: 'W', label: '수프라' },
  { value: 'ST', label: '세르지오 타키니' },
] as const;

const FIRST_YEAR = 2013;

/** 올해부터 2013년까지 내림차순 */
export const getShopManagerFeeYears = (
  currentYear = new Date().getFullYear(),
): string[] =>
  Array.from({ length: Math.max(currentYear - FIRST_YEAR + 1, 0) }, (_, i) =>
    String(currentYear - i),
  );

export const SHOP_MANAGER_FEE_MONTHS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1),
);

export const SHOP_MANAGER_FEE_NOT_LOADED_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

export type ShopManagerFeeItem = Record<string, string>;

/** SAP 원문 — ITEM 매장별 행, HEAD 합계 */
export type ShopManagerFeeSource = {
  HEAD?: Record<string, string> | null;
  ITEM?: ShopManagerFeeItem[] | null;
};

export type ShopManagerFeeDraftSource = {
  code: number;
  display: { source?: ShopManagerFeeSource | null };
};

/** 저장 attributes — content 는 조회 화면이 그대로 보여 주는 표 HTML, data 는 SAP 원문 */
export type ShopManagerFeeState = {
  content: string;
  data: ShopManagerFeeSource;
};

export const buildShopManagerFeeSubject = (
  t: TFunction,
  brandLabel: string,
  year: string,
  month: string,
): string =>
  t('{{brand}} {{year}}년 {{month}}월 중간 관리 수수료 정산의 건', {
    brand: brandLabel,
    year,
    month,
  });

/** 레거시 근무시작일 표기 — YYYYMMDD → YYYY.MM.DD */
export const formatShopManagerStartDate = (value: string | undefined): string =>
  value ? `${value.slice(0, 4)}.${value.slice(4, 6)}.${value.slice(6, 8)}` : '';

/** 저장 attributes.content. 없거나 빈 문자열이면 null */
export const parseShopManagerFeeContent = (
  attributes: unknown,
): string | null => {
  if (!attributes || typeof attributes !== 'object') return null;
  const raw = (attributes as Record<string, unknown>).content;
  return typeof raw === 'string' && raw.trim() ? raw : null;
};

/** 레거시와 같이 매장 행이 1건 이상 조회돼야 상신 가능 */
export const checkShopManagerFeeBeforeSave = (
  state: ShopManagerFeeState | null | undefined,
): string | null =>
  state?.content && state.data.ITEM?.length
    ? null
    : SHOP_MANAGER_FEE_NOT_LOADED_MESSAGE;

export const buildShopManagerFeeAttributes = (
  state: ShopManagerFeeState | null | undefined,
): ApprovalAttributes | null =>
  state ? { content: state.content, data: state.data } : null;
